import { normalizeLanguage, type AppLanguage } from '@/locales/locale'

export interface GuideStep {
    number: string
    title: string
    description: string
}

export interface GuideCopy {
    nav: {
        label: string
        items: Array<{ id: string; label: string }>
    }
    hero: {
        titleLines: [string, string]
        description: string
    }
    wear: {
        title: string
        description: string
        points: Array<{ title: string; description: string }>
        imageAlt: string
    }
    dashboard: {
        title: string
        description: string
        steps: GuideStep[]
    }
    response: {
        title: string
        description: string
        steps: GuideStep[]
    }
    users: {
        title: string
        description: string
        items: string[]
        imageAlt: string
    }
    safeZone: {
        title: string
        description: string
        steps: GuideStep[]
        notice: string
    }
    cta: {
        title: string
        description: string
        goToService: string
        inquire: string
    }
}

const ko: GuideCopy = {
    nav: {
        label: '사용 안내 바로가기',
        items: [
            { id: 'wear', label: '기기 착용' },
            { id: 'dashboard', label: '대시보드' },
            { id: 'response', label: '비상 대응' },
            { id: 'users', label: '대상자 관리' },
            { id: 'safe-zone', label: '안심구역' }
        ]
    },
    hero: {
        titleLines: ['관제 담당자를 위한', '케얼리 빠른 사용 안내'],
        description: '착용과 연결 상태 확인부터 긴급 알림 처리까지, 실제 관제 업무에 필요한 순서대로 정리했습니다.'
    },
    wear: {
        title: '착용과 연결 상태 확인',
        description: '공기 주머니가 가슴 또는 복부에 밀착되도록 상체에 두르고 고정합니다. 전원을 켠 뒤 관제 화면에서 연결과 배터리 상태를 확인하세요.',
        points: [
            { title: '밀착 착용', description: '센서가 몸의 움직임을 안정적으로 감지하도록 밴드를 조절합니다.' },
            { title: '긴급 호출', description: '도움이 필요하면 기기 긴급 버튼을 3초 이상 누릅니다.' },
            { title: '배터리 확인', description: '업무 시작 전 배터리와 충전 상태를 확인합니다.' },
            { title: '물 접촉 주의', description: '샤워·수영·침수 환경에서는 기기를 분리합니다.' }
        ],
        imageAlt: '케얼리 의류형 기기와 공기압 센서 구동부'
    },
    dashboard: {
        title: '대시보드 확인 순서',
        description: '위험도가 높은 정보부터 확인하도록 화면이 구성되어 있습니다.',
        steps: [
            {
                number: '01',
                title: '전체 상태부터 확인',
                description: '상단 요약 카드에서 전체 대상자, 긴급 버튼, 낙상 감지, 생체 이상 건수를 먼저 확인합니다.'
            },
            {
                number: '02',
                title: '지도에서 위치 확인',
                description: '대상자 마커를 선택해 현재 위치와 상태를 확인하고, 센터 위치 버튼으로 관제 기준 위치로 돌아옵니다.'
            },
            {
                number: '03',
                title: '실시간 알림 처리',
                description: '오른쪽 알림 패널에서 이상 생체 신호를 확인한 뒤 위치 확인 또는 처리 버튼으로 대응을 시작합니다.'
            }
        ]
    },
    response: {
        title: '비상 알림 대응 절차',
        description: '기관별 운영 정책과 현장 상황에 맞춰 담당 연락망과 출동 절차를 적용하세요.',
        steps: [
            {
                number: '01',
                title: '알림 종류 확인',
                description: '긴급 요청, 낙상 감지, 생체 이상 중 어떤 신호인지와 발생 시간을 확인합니다.'
            },
            {
                number: '02',
                title: '대상자 상태·위치 확인',
                description: '대상자에게 연락하고 지도 위치, 보호자 연락처, 최근 생체 정보를 함께 확인합니다.'
            },
            {
                number: '03',
                title: '필요한 대응 연계',
                description: '상황에 따라 보호자·현장 담당자·119에 전달하고 조치 결과와 메모를 남깁니다.'
            },
            {
                number: '04',
                title: '처리 완료 기록',
                description: '담당자, 확인 시간, 처리 결과를 저장해 이후 이력 조회가 가능하도록 마무리합니다.'
            }
        ]
    },
    users: {
        title: '대상자 상세 정보 활용',
        description: '목록에서 대상자를 선택하면 기본 정보, 비상 연락처, 위치, 기기 상태와 생체 추이를 한 화면에서 확인할 수 있습니다.',
        items: [
            '이름 검색과 선택 상태로 대상자 빠르게 찾기',
            '배터리·온라인·충전·긴급 상태 확인',
            '심박·호흡·걸음·낙상 추이 확인',
            '상세 지도에서 최근 위치와 이동 정보 확인'
        ],
        imageAlt: '케얼리 중앙관제와 119 대응 서비스 구성'
    },
    safeZone: {
        title: '안심구역 설정과 이탈 대응',
        description: '모바일 앱에서 생활 반경을 안심구역으로 지정하면 대상자가 구역을 벗어났을 때 알림을 받을 수 있습니다. 위치 권한과 GPS가 켜져 있어야 합니다.',
        steps: [
            { number: '01', title: '대상자 선택', description: '모바일 앱에서 관리할 대상자를 선택한 뒤 설정의 안심구역 메뉴로 이동합니다.' },
            { number: '02', title: '중심 위치 지정', description: '주소를 검색하거나 지도에서 생활 거점의 중심 위치를 정확히 지정합니다.' },
            { number: '03', title: '반경 설정·저장', description: '기관 운영 기준에 맞는 반경을 선택하고 저장한 뒤 지도에 표시된 범위를 다시 확인합니다.' },
            { number: '04', title: '이탈 알림 대응', description: '알림 발생 시 최근 위치와 시간을 확인하고 대상자·보호자에게 연락한 뒤 조치 내용을 기록합니다.' }
        ],
        notice: 'GPS 오차나 통신 지연으로 실제 위치와 차이가 날 수 있습니다. 이탈 알림만으로 상황을 단정하지 말고 반드시 대상자 또는 보호자에게 확인하세요.'
    },
    cta: {
        title: '케얼리 관제를 시작하세요',
        description: '서비스 이용 계정 또는 도입 상담이 필요하면 아래에서 바로 시작할 수 있습니다.',
        goToService: '관제 서비스 바로가기',
        inquire: '도입 문의하기'
    }
}

const en: GuideCopy = {
    nav: {
        label: 'Quick start shortcuts',
        items: [
            { id: 'wear', label: 'Wearing the device' },
            { id: 'dashboard', label: 'Dashboard' },
            { id: 'response', label: 'Emergency response' },
            { id: 'users', label: 'Member management' },
            { id: 'safe-zone', label: 'Safe zone' }
        ]
    },
    hero: {
        titleLines: ['Carearly quick start guide', 'for monitoring staff'],
        description: 'From checking the fit and connection to handling emergency alerts, everything is laid out in the order your monitoring work actually follows.'
    },
    wear: {
        title: 'Check the fit and connection',
        description: 'Wrap the device around your upper body and fasten it so the air cell sits snugly against the chest or abdomen. After turning it on, check the connection and battery status on the monitoring screen.',
        points: [
            { title: 'Snug fit', description: 'Adjust the band so the sensor can reliably detect body movement.' },
            { title: 'Emergency call', description: 'If you need help, press and hold the device’s emergency button for 3 seconds or more.' },
            { title: 'Check the battery', description: 'Check the battery and charging status before starting your shift.' },
            { title: 'Keep away from water', description: 'Remove the device when showering, swimming or in environments where it could be submerged.' }
        ],
        imageAlt: 'Carearly wearable device and its pneumatic sensor controller'
    },
    dashboard: {
        title: 'Dashboard checking order',
        description: 'The screen is arranged so you can review the highest-risk information first.',
        steps: [
            {
                number: '01',
                title: 'Start with the overall status',
                description: 'In the summary cards at the top, first check the total number of people and the counts of emergency button presses, fall detections and biometric anomalies.'
            },
            {
                number: '02',
                title: 'Check locations on the map',
                description: 'Select a person’s marker to see their current location and status, and use the center location button to return to the monitoring base position.'
            },
            {
                number: '03',
                title: 'Handle live alerts',
                description: 'In the alert panel on the right, review abnormal biometric signals, then start your response with the location check or process button.'
            }
        ]
    },
    response: {
        title: 'Emergency alert response procedure',
        description: 'Apply your contact network and dispatch procedures according to your organization’s operating policy and the situation on site.',
        steps: [
            {
                number: '01',
                title: 'Identify the alert type',
                description: 'Check whether the signal is an emergency request, a fall detection or a biometric anomaly, and when it occurred.'
            },
            {
                number: '02',
                title: 'Check the person’s status and location',
                description: 'Contact the person and review their map location, guardian contact and recent biometric information together.'
            },
            {
                number: '03',
                title: 'Arrange the necessary response',
                description: 'Depending on the situation, notify the guardian, on-site staff or emergency services (119), and leave a record of the action taken and a memo.'
            },
            {
                number: '04',
                title: 'Record completion',
                description: 'Save the staff member, confirmation time and outcome so the event can be looked up in the history later.'
            }
        ]
    },
    users: {
        title: 'Using member details',
        description: 'Select a member from the list to see basic information, emergency contacts, location, device status and biometric trends on a single screen.',
        items: [
            'Find a member quickly with name search and selection',
            'Check battery, online, charging and emergency status',
            'Review heart rate, respiration, step and fall trends',
            'See recent location and movement on the detail map'
        ],
        imageAlt: 'Carearly central monitoring and 119 emergency response service structure'
    },
    safeZone: {
        title: 'Safe zone setup and exit response',
        description: 'When you set a person’s living area as a safe zone in the mobile app, you can receive an alert if they leave it. Location permission and GPS must be turned on.',
        steps: [
            { number: '01', title: 'Select a person', description: 'In the mobile app, select the person you manage, then go to the Safe Zone menu in settings.' },
            { number: '02', title: 'Set the center point', description: 'Search for an address or pinpoint the center of their daily base on the map.' },
            { number: '03', title: 'Set the radius and save', description: 'Choose a radius that fits your organization’s operating standards, save it, and check the range shown on the map once more.' },
            { number: '04', title: 'Respond to exit alerts', description: 'When an alert occurs, check the latest location and time, contact the person and their guardian, and record the action taken.' }
        ],
        notice: 'The actual location may differ because of GPS error or communication delay. Do not rely on an exit alert alone to judge the situation — always confirm with the person or their guardian.'
    },
    cta: {
        title: 'Start monitoring with Carearly',
        description: 'If you need a service account or an adoption consultation, you can get started right below.',
        goToService: 'Go to the monitoring service',
        inquire: 'Talk to sales'
    }
}

const ja: GuideCopy = {
    nav: {
        label: '使い方ガイドのショートカット',
        items: [
            { id: 'wear', label: '機器の着用' },
            { id: 'dashboard', label: 'ダッシュボード' },
            { id: 'response', label: '緊急対応' },
            { id: 'users', label: '対象者管理' },
            { id: 'safe-zone', label: '安心区域' }
        ]
    },
    hero: {
        titleLines: ['管制担当者のための', 'Carearly クイックスタートガイド'],
        description: '機器の着用と接続状態の確認から緊急通知の対応まで、実際の管制業務に必要な順序でまとめました。'
    },
    wear: {
        title: '着用と接続状態の確認',
        description: 'エアセルが胸または腹部に密着するように上半身に巻いて固定します。電源を入れたら、管制画面で接続とバッテリーの状態を確認してください。',
        points: [
            { title: '密着して着用', description: 'センサーが体の動きを安定して検知できるようにバンドを調整します。' },
            { title: '緊急呼び出し', description: '助けが必要なときは、機器の緊急ボタンを3秒以上押します。' },
            { title: 'バッテリー確認', description: '業務を始める前にバッテリーと充電の状態を確認します。' },
            { title: '水への接触に注意', description: 'シャワー・水泳・浸水のおそれがある環境では機器を取り外します。' }
        ],
        imageAlt: 'Carearlyの衣類型デバイスと空気圧センサーの制御部'
    },
    dashboard: {
        title: 'ダッシュボードの確認手順',
        description: 'リスクの高い情報から確認できるように画面が構成されています。',
        steps: [
            {
                number: '01',
                title: '全体の状態から確認',
                description: '上部のサマリーカードで、対象者の総数、緊急ボタン、転倒検知、生体異常の件数を最初に確認します。'
            },
            {
                number: '02',
                title: '地図で位置を確認',
                description: '対象者のマーカーを選択して現在位置と状態を確認し、センター位置ボタンで管制の基準位置に戻ります。'
            },
            {
                number: '03',
                title: 'リアルタイム通知への対応',
                description: '右側の通知パネルで異常な生体信号を確認し、位置確認または対応ボタンで対応を開始します。'
            }
        ]
    },
    response: {
        title: '緊急通知への対応手順',
        description: '施設ごとの運用方針と現場の状況に合わせて、担当の連絡網と出動手順を適用してください。',
        steps: [
            {
                number: '01',
                title: '通知の種類を確認',
                description: '緊急要請、転倒検知、生体異常のどの信号か、また発生時刻を確認します。'
            },
            {
                number: '02',
                title: '対象者の状態・位置を確認',
                description: '対象者に連絡し、地図上の位置、保護者の連絡先、直近の生体情報をあわせて確認します。'
            },
            {
                number: '03',
                title: '必要な対応を連携',
                description: '状況に応じて保護者・現場担当者・救急(119)へ伝え、対応結果とメモを残します。'
            },
            {
                number: '04',
                title: '対応完了を記録',
                description: '担当者、確認時刻、対応結果を保存し、後から履歴を確認できるようにして完了します。'
            }
        ]
    },
    users: {
        title: '対象者の詳細情報の活用',
        description: '一覧から対象者を選択すると、基本情報、緊急連絡先、位置、機器の状態、生体データの推移を一つの画面で確認できます。',
        items: [
            '名前検索と選択状態で対象者をすばやく探す',
            'バッテリー・オンライン・充電・緊急の状態を確認',
            '心拍・呼吸・歩数・転倒の推移を確認',
            '詳細マップで直近の位置と移動情報を確認'
        ],
        imageAlt: 'Carearlyの中央管制と119対応サービスの構成'
    },
    safeZone: {
        title: '安心区域の設定と離脱時の対応',
        description: 'モバイルアプリで生活圏を安心区域に指定すると、対象者が区域の外に出たときに通知を受け取れます。位置情報の権限とGPSをオンにしておく必要があります。',
        steps: [
            { number: '01', title: '対象者を選択', description: 'モバイルアプリで管理する対象者を選択し、設定の安心区域メニューに移動します。' },
            { number: '02', title: '中心位置を指定', description: '住所を検索するか、地図上で生活拠点の中心位置を正確に指定します。' },
            { number: '03', title: '半径の設定・保存', description: '施設の運用基準に合った半径を選択して保存し、地図に表示された範囲をもう一度確認します。' },
            { number: '04', title: '離脱通知への対応', description: '通知が発生したら直近の位置と時刻を確認し、対象者・保護者に連絡して対応内容を記録します。' }
        ],
        notice: 'GPSの誤差や通信の遅延により、実際の位置と差が出ることがあります。離脱通知だけで状況を断定せず、必ず対象者または保護者に確認してください。'
    },
    cta: {
        title: 'Carearly管制を始めましょう',
        description: 'サービス利用アカウントや導入相談が必要な場合は、以下からすぐに始められます。',
        goToService: '管制サービスへ移動',
        inquire: '導入のお問い合わせ'
    }
}

const cn: GuideCopy = {
    nav: {
        label: '使用指南快捷导航',
        items: [
            { id: 'wear', label: '设备穿戴' },
            { id: 'dashboard', label: '仪表盘' },
            { id: 'response', label: '紧急响应' },
            { id: 'users', label: '对象管理' },
            { id: 'safe-zone', label: '安全区域' }
        ]
    },
    hero: {
        titleLines: ['为管控人员准备的', 'Carearly 快速使用指南'],
        description: '从穿戴与连接状态确认到紧急通知处理，按照实际管控工作所需的顺序进行了整理。'
    },
    wear: {
        title: '确认穿戴与连接状态',
        description: '将设备围在上身并固定好，使气囊紧贴胸部或腹部。开机后，请在管控界面确认连接和电量状态。',
        points: [
            { title: '贴身穿戴', description: '调整束带，使传感器能够稳定感知身体的活动。' },
            { title: '紧急呼叫', description: '需要帮助时，长按设备紧急按钮 3 秒以上。' },
            { title: '确认电量', description: '开始工作前，确认电量和充电状态。' },
            { title: '避免接触水', description: '淋浴、游泳或可能进水的环境中，请取下设备。' }
        ],
        imageAlt: 'Carearly 服装式设备及气压传感器控制器'
    },
    dashboard: {
        title: '仪表盘查看顺序',
        description: '页面按照优先查看风险较高信息的方式布局。',
        steps: [
            {
                number: '01',
                title: '先确认整体状态',
                description: '先在顶部汇总卡片中查看对象总数，以及紧急按钮、跌倒检测和生命体征异常的数量。'
            },
            {
                number: '02',
                title: '在地图上确认位置',
                description: '选择对象标记查看其当前位置和状态，并通过中心位置按钮回到管控基准位置。'
            },
            {
                number: '03',
                title: '处理实时通知',
                description: '在右侧通知面板中确认异常生物信号，然后通过位置确认或处理按钮开始响应。'
            }
        ]
    },
    response: {
        title: '紧急通知响应流程',
        description: '请根据各机构的运营政策和现场情况，应用相应的联络网和出动流程。',
        steps: [
            {
                number: '01',
                title: '确认通知类型',
                description: '确认是紧急求助、跌倒检测还是生命体征异常，以及发生时间。'
            },
            {
                number: '02',
                title: '确认对象状态与位置',
                description: '联系对象，并同时确认地图位置、监护人联系方式和最近的生物信息。'
            },
            {
                number: '03',
                title: '联动所需响应',
                description: '根据情况通知监护人、现场负责人或 119，并记录处理结果和备注。'
            },
            {
                number: '04',
                title: '记录处理完成',
                description: '保存负责人、确认时间和处理结果，以便日后查询记录并完成收尾。'
            }
        ]
    },
    users: {
        title: '对象详情的使用',
        description: '在列表中选择对象后，可在同一界面查看基本信息、紧急联系人、位置、设备状态和生物数据趋势。',
        items: [
            '通过姓名搜索和选中状态快速找到对象',
            '确认电量、在线、充电和紧急状态',
            '查看心率、呼吸、步数和跌倒趋势',
            '在详情地图中查看最近位置和移动信息'
        ],
        imageAlt: 'Carearly 中央管控与 119 响应服务构成'
    },
    safeZone: {
        title: '安全区域设置与离开响应',
        description: '在手机应用中将生活范围指定为安全区域后，对象离开该区域时即可收到通知。需要开启位置权限和 GPS。',
        steps: [
            { number: '01', title: '选择对象', description: '在手机应用中选择要管理的对象，然后进入设置中的安全区域菜单。' },
            { number: '02', title: '指定中心位置', description: '搜索地址，或在地图上准确指定生活据点的中心位置。' },
            { number: '03', title: '设置半径并保存', description: '选择符合机构运营标准的半径并保存，然后再次确认地图上显示的范围。' },
            { number: '04', title: '响应离开通知', description: '通知发生时，确认最近的位置和时间，联系对象及监护人，并记录处理内容。' }
        ],
        notice: '由于 GPS 误差或通信延迟，实际位置可能存在偏差。请勿仅凭离开通知断定情况，务必向对象或监护人确认。'
    },
    cta: {
        title: '开始使用 Carearly 管控',
        description: '如需服务使用账号或引进咨询，可在下方直接开始。',
        goToService: '前往管控服务',
        inquire: '咨询引进方案'
    }
}

const GUIDE_COPY: Record<AppLanguage, GuideCopy> = { ko, en, ja, cn }

export const getGuideCopy = (language?: string | null): GuideCopy => GUIDE_COPY[normalizeLanguage(language)]
